import { Badge, Tooltip } from '@lib/components'
import { type ArtBoardMode, artBoardActions } from '@store/reducers/artboard'
import { openModal } from '@storeOld/reducers/ui'
import cx from 'classnames'
import type React from 'react'
import type { CSSProperties } from 'react'
import { useDispatch } from 'react-redux'

import { type GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { MODAL_UPGRADE_PLAN } from '../../../../App/Modal/constants'
import {
  type SidebarTab,
  useGetSidebarSwitcher,
} from '../../hook/useGetSidebarSwitcher'
import css from './SettingsSwitcher.module.scss'

type Props = {
  mode: ArtBoardMode
}

export const SettingsSwitcher: React.FC<Props> = ({ mode }) => {
  const dispatch = useDispatch()
  const tabs = useGetSidebarSwitcher()

  const handleClick = (tab: SidebarTab) => {
    if (tab.disabled) {
      return
    }

    dispatch(artBoardActions.changeMode(tab.mode))
  }

  const handleBadgeClick = (
    mode: ArtBoardMode,
    gtmTrigger?: GtmTriggerName
  ) => {
    gtmTrigger && sendGtmEvent(gtmTrigger)

    if (mode === 'symbol_library') {
      dispatch(
        openModal({
          modalType: MODAL_UPGRADE_PLAN,
          modalProps: {
            slideType: 'symbol',
          },
        })
      )
      return
    }

    dispatch(openModal(MODAL_UPGRADE_PLAN))
  }

  const visibleTabs = tabs.filter((tab) => tab.visible)

  // The underline slides between tabs by the active index, CSS does the rest.
  const style = {
    '--tabs': visibleTabs.length,
    '--active-tab': visibleTabs.findIndex((tab) => tab.mode === mode),
  } as CSSProperties

  return (
    <div className={css.switcher} style={style}>
      {visibleTabs.map((tab) => (
        <Tooltip key={tab.mode} content={tab.tooltip}>
          <button
            className={cx(css.item, {
              [css.active]: mode === tab.mode,
              [css.disabled]: tab.disabled,
              [css.badge]: tab.badge,
            })}
            onClick={() => handleClick(tab)}
          >
            {tab.icon}

            {tab.badge && (
              <div
                onClick={() => handleBadgeClick(tab.mode, tab.gtmProBadgeEvent)}
                className={css.badge}
              >
                <Badge type={tab.badge} />
              </div>
            )}
          </button>
        </Tooltip>
      ))}

      <div className={css.underline} />
    </div>
  )
}
